import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "../../widgets/modal";
import TextField from "../../widgets/textField/textField";
import { DATE_FORMAT_UPDATED } from "../../constants/app.constants";
import { updateSingleApprovalQuote } from "../quotes/quotes.utils";
import { FC } from "react";
import Swal from "sweetalert2";
import Loader from "../../widgets/Loader";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";

type QuoteApprovalModalProps = {
  isOpen: boolean;
  onClose: () => void;
  quoteId: number;
};

type UpdatedValue = string | Dayjs;

const QuoteApproveModal: FC<QuoteApprovalModalProps> = ({
  quoteId,
  isOpen,
  onClose,
}) => {
  const [dateApproved, setDateApproved] = useState<Dayjs | null>(null);
  const [customerPO, setCustomerPO] = useState("");
  const [isShowLoader, setIsShowLoader] = useState(false);
  const [validationMsg, setValidationMsg] = useState("");
  const approvedBy = window.localStorage.getItem("ApproveByName") || "";
  const approvedById = window.localStorage.getItem("ApproveByID") || "";

  // Keeping the approve popup-open API code commented in case we need it again in future.
  // import { BENCHMARK_SINGLEAPPROVALQUOTE } from "../../constants/app.constants";
  // import { fetchApprovalQuote } from "../quotes/quotes.utils";
  // import { useQuery } from "@tanstack/react-query";
  // const { isFetching, data: dataBenchmarkSingleApprovalQuote } = useQuery({
  //   queryKey: [BENCHMARK_SINGLEAPPROVALQUOTE, quoteId],
  //   queryFn: () => fetchApprovalQuote(quoteId),
  //   enabled: isOpen,
  //   refetchOnWindowFocus: false,
  // });
  const isFetching = false;

  const onChangeFieldValues = useCallback(
    (id: string, updatedValue: UpdatedValue) => {
      if (id === "dateApproved") {
        setDateApproved(updatedValue as Dayjs);
      } else if (id === "customerPO") {
        setCustomerPO(updatedValue as string);
      }
    },
    []
  );

  useEffect(() => {
    if (quoteId) {
      setCustomerPO("");
      setDateApproved(dayjs());
      setValidationMsg("");
      setIsShowLoader(false);
    }
  }, [quoteId]);

  const handleSubmit = async () => {
    if (!approvedBy) {
      setValidationMsg("Please Enter Approved By");
    } else if (!dateApproved || !dateApproved.isValid()) {
      setValidationMsg("Please select Approved date");
    } else {
      if (quoteId) {
        setIsShowLoader(true);
        const result = await updateSingleApprovalQuote(
          quoteId,
          approvedBy,
          approvedById,
          customerPO,
          dateApproved.toDate()
        );
        setIsShowLoader(false);
        if (result === "Success") {
          Swal.fire({
            customClass: { popup: "sweetAlert" },
            icon: "success",
            showConfirmButton: false,
            timer: 1500,
            title: "Quote Updated Successfully!",
          });
          onClose();
        } else if (result === "mailnotsent") {
          Swal.fire({
            customClass: ".swal2-confirm",
            icon: "warning",
            title:
              "Quote approved but mail not sent to Benchmark, please contact notifications@benchmarkcos.com",
          });
        } else {
          Swal.fire({
            customClass: ".swal2-confirm",
            icon: "warning",
            title: "Failed to Update Quote",
          });
        }
      }
    }
  };

  const handleCancel = () => {
    setDateApproved(null);
    setCustomerPO("");
    setIsShowLoader(false);
    setValidationMsg("");
    onClose();
  };

  return (
    <Modal show={isOpen} label="Approval" onClose={handleCancel}>
      {validationMsg && <div className="text-danger">{validationMsg}</div>}
      <div className="quoteModalSection">
        <div className="modalForm">
          {(isShowLoader || isFetching) && <Loader />}
          <form>
            <div className="formControlGroup">
              <label className="formFieldLabel">
                Approved By<span className="text-danger">*</span>
              </label>
              <div className="formField">
                <TextField
                  id="approvedBy"
                  onChange={onChangeFieldValues}
                  type="text"
                  value={approvedBy}
                  disabled
                />
              </div>
            </div>

            <div className="formControlGroup">
              <label className="formFieldLabel">
                Date Approved<span className="text-danger">*</span>
              </label>
              <div className="formField">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    disabled
                    disableFuture
                    value={dateApproved}
                    format={DATE_FORMAT_UPDATED}
                    slotProps={{
                      textField: {
                        size: "small",
                        className: "date-input-field",
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>
            </div>

            <div className="formControlGroup">
              <label className="formFieldLabel">Customer PO</label>
              <div className="formField">
                <TextField
                  id="customerPO"
                  onChange={onChangeFieldValues}
                  type="text"
                  value={customerPO}
                />
              </div>
            </div>

            <div className="formControlGroup">
              <label className="formFieldLabel" />
              <div className="formField actionButtons">
                <button
                  type="button"
                  className="btn cancelPwdBtn"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn submitBtn"
                  onClick={handleSubmit}
                >
                  Submit
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default QuoteApproveModal;
